import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';

import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';

import AddLeadSource from 'modules/lead/AddLead/components/CaseInformation/AddLeadSource';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import { updateLeadDetails } from 'services/lead/leadServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useToggle } from 'hooks/useToggle';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { gratitudeOptions } from 'constants/dropdownOptions';
import AddReferral from 'modules/lead/AddLead/components/CaseInformation/AddReferral';
import { queryClient } from 'config/queryClient';
import { queryKeys } from 'modules/LeadsV2/config/queryClient';

export default function Summary(props) {
  const { leadDetails, loadLeadSources, leadSourceList, onCaseClose, leadStages, loadLeadDetails, loadLeadReferrals, leadReferralsList } =
    props;
  const [sourceToggle, setSourceToggle] = useToggle();
  const [apiLoading, setApiLoading] = useToggle();
  const [onSourceReferral, setOnSourceReferral] = useToggle(false);
  const [referralToggle, setReferralToggle] = useToggle();
  const [gratitudeToggle, setGratitudeToggle] = useToggle(leadDetails?.lead_details?.gratitude === 'yes');
  const { addToast } = useToast();

  const { control, reset, handleSubmit, setValue, watch } = useForm();

  useEffect(() => {
    if (leadDetails) {
      reset({
        ...leadDetails,
        last_login: leadDetails?.main_client[0]?.last_login
          ? moment(leadDetails?.main_client[0]?.last_login).format('MM/DD/YYYY, hh:mm a')
          : '--',
      });
    }
  }, [leadDetails]);

  const leadSource = watch('lead_details.source');
  useEffect(() => {
    let lead_source_id = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;
    if (leadSource === lead_source_id) {
      setOnSourceReferral(true);
      const gratitude = watch('lead_details.gratitude');
      if (gratitude !== 'yes') {
        setGratitudeToggle(false);
      }
    } else {
      setOnSourceReferral(false);
    }
  }, [leadSource, leadSourceList]);

  const sourceAddOnSuccess = (data) => {
    if (data) {
      setValue('lead_details.source', data?.Lead_source_name);
    }
    loadLeadSources();
    setSourceToggle(false);
  };

  const referralAddOnSuccess = (data) => {
    if (data) {
      setValue('referred_from', data);
    }
    loadLeadReferrals();
    setReferralToggle(false);
  };

  const updateLead = (formData) => {
    const { case_description, lead_details, lead_stage, lead_status, sk, referred_from } = formData || {};
    const referralSourceId = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;
    const updatedLeadDetails = { ...lead_details };
    if (updatedLeadDetails.source !== referralSourceId) {
      updatedLeadDetails.gratitude = null;
      updatedLeadDetails.gratitude_item = '';
    }
    setApiLoading(true);
    updateLeadDetails(sk, {
      case_description,
      lead_details: updatedLeadDetails,
      lead_stage,
      lead_status,
      referred_from: updatedLeadDetails.source !== referralSourceId ? null : referred_from,
    })
      .then(() => {
        setApiLoading(false);
        loadLeadDetails();
        queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
      })
      .catch(() => {
        setApiLoading(false);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
      });
  };

  return (
    <React.Fragment>
      <h5 className="caption-bold">SUMMARY</h5>
      <div className="card-bg lead-data-box p-3">
        <div className="row">
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Last Login</div>
              <div className="col-lg-8 col-12">
                <Controller
                  name="last_login"
                  control={control}
                  render={({ field }) => <InputText disabled id={field.name} {...field} className="w-100 input-shadow" />}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Lead Stage</div>
              <div className="col-lg-8 col-12">
                <Controller
                  name="lead_stage"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id={field.name}
                      onChange={(e) => {
                        typeof onCaseClose === 'function' && onCaseClose(e.value);
                        field.onChange(e.value);
                      }}
                      value={field.value}
                      options={leadStages}
                      optionLabel="lead_stage_name"
                      optionValue="sk"
                      placeholder="Select"
                      className="outline-dropdown w-100"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Lead Priority</div>
              <div className="col-lg-8 col-12">
                <Controller
                  name="lead_details.priority"
                  control={control}
                  render={({ field }) => (
                    <PriorityDropdown
                      placeholder="Select"
                      id={field.name}
                      onChange={(e) => field.onChange(e.value)}
                      value={field.value}
                      className="outline-dropdown w-100"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Potential Case Value</div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="case_description.potential_case_value"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      className="w-100 input-shadow"
                      inputId="currency-us"
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      id={field.name}
                      onChange={(e) => field.onChange(e.value)}
                      value={field.value}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Lead Source</div>
              <div className="col-lg-8 col-12">
                {sourceToggle ? (
                  <AddLeadSource onSuccess={sourceAddOnSuccess} onDismiss={setSourceToggle} />
                ) : (
                  <div className="d-flex align-items-center">
                    <Controller
                      name="lead_details.source"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          id={field.name}
                          onChange={(e) => {
                            field.onChange(e.value);
                          }}
                          value={field.value}
                          optionLabel="source"
                          optionValue="lead_source_id"
                          options={leadSourceList}
                          placeholder="Select"
                          className="input-shadow w-100"
                          filter
                        />
                      )}
                    />
                    <span className={classNames({ pointer: true })}>
                      <i className="fas fa-plus-circle ms-2" onClick={setSourceToggle} />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {onSourceReferral && (
            <div className="col-md-6">
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-lg-4 col-12 p-medium">Referred From</div>
                <div className="col-lg-8 col-12">
                  {referralToggle ? (
                    <AddReferral onSuccess={referralAddOnSuccess} onDismiss={setReferralToggle} />
                  ) : (
                    <div className="d-flex align-items-center">
                      <Controller
                        name="referred_from"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            id={field.name}
                            onChange={(e) => {
                              field.onChange(e.value);
                            }}
                            value={field.value}
                            optionLabel="referral_entity"
                            optionValue="referral_entity_id"
                            options={leadReferralsList.filter((item) => item.referral_entity !== '')}
                            placeholder="Select"
                            className="input-shadow w-100"
                            filter
                          />
                        )}
                      />
                      <span className={classNames({ pointer: true })}>
                        <i className="fas fa-plus-circle ms-2" onClick={setReferralToggle} />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {onSourceReferral && (
            <div className="col-md-6">
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">Gratitude</div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="lead_details.gratitude"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        id={field.name}
                        onChange={(e) => {
                          if (e.value === 'yes') {
                            setGratitudeToggle(true);
                          } else {
                            setGratitudeToggle(false);
                            setValue('lead_details.gratitude_item', null);
                          }
                          field.onChange(e.value);
                        }}
                        value={leadDetails.gratitude || field.value}
                        optionLabel="label"
                        optionValue="value"
                        options={gratitudeOptions}
                        placeholder="Select"
                        className="input-shadow w-100"
                        filter
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {onSourceReferral && gratitudeToggle && (
            <div className="col-md-6">
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 p-medium">Gratitude Item</div>
                <div className="col-md-8 col-12">
                  <Controller
                    name={`lead_details.gratitude_item`}
                    defaultValue={leadDetails.gratitude_item}
                    control={control}
                    render={({ field }) => (
                      <TextSnippetWrapper type="text" placeholder="Type in" {...field} className="input-shadow border-0 w-100" />
                    )}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <>
          <hr />
          <div className="d-flex justify-content-end">
            <Button label="Save Changes" className="p-button p-button-primary" loading={apiLoading} onClick={handleSubmit(updateLead)} />
          </div>
        </>
      </div>
    </React.Fragment>
  );
}
